// Session management for conversation continuity
let currentSessionId = null

// Get or create session ID
export function getSessionId() {
  console.log('🔍 getSessionId() called')
  console.log('🔍 currentSessionId:', currentSessionId)
  console.log(
    '🔍 localStorage ai_session_id:',
    localStorage.getItem('ai_session_id')
  )

  if (currentSessionId) {
    console.log('✅ Using existing in-memory session ID:', currentSessionId)
    return currentSessionId
  }

  // Try to get from localStorage for persistence across page reloads
  const storedSessionId = localStorage.getItem('ai_session_id')
  if (storedSessionId) {
    currentSessionId = storedSessionId
    console.log('✅ Restored session ID from localStorage:', currentSessionId)
    return currentSessionId
  }

  console.log('❌ No existing session ID - will create new session')
  return null // Let the API create a new session
}

// Store session ID for future requests
export function storeSessionId(sessionId) {
  console.log('storeSessionId called with:', sessionId)
  if (sessionId) {
    currentSessionId = sessionId
    try {
      localStorage.setItem('ai_session_id', sessionId)
      console.log('✅ Successfully stored session ID:', sessionId)
      console.log(
        '✅ localStorage now contains:',
        localStorage.getItem('ai_session_id')
      )
    } catch (e) {
      console.error('❌ Failed to store session ID in localStorage:', e)
    }
  } else {
    console.warn('⚠️ storeSessionId called with null/undefined sessionId')
  }
}

// Clear session (useful for starting fresh conversations)
export function clearSession() {
  currentSessionId = null
  localStorage.removeItem('ai_session_id')
  console.log('Session cleared - next request will start new conversation')
}

export async function giveMeAnswer(text, images, selectedModel) {
  const apiUrl = 'https://smart-assistant-production.up.railway.app/api/prompt'

  const cleanData = await this.call(
    'getCleanCanvasScope',
    this.getCanvasScope()
  )
  const data = Object.fromEntries(
    Object.entries(cleanData).filter(
      ([_, value]) => value !== undefined && value !== null
    )
  )
  const requestBody = {
    query: text,
    data,
    model: selectedModel || 'Starter' // Default to Free if no model specified
  }

  const selectedResource = this.getChosenKey() || this.getSelectedKey()
  if (selectedResource) requestBody.selectedResource = selectedResource
  const selectedFunction = this.getCanvasScope().activeFunction
  if (selectedFunction) requestBody.selectedFunction = selectedFunction

  // Add session ID for conversation continuity
  console.log('🔍 Building request - checking for session ID...')
  const sessionId = getSessionId()
  if (sessionId) {
    requestBody.session_id = sessionId
    console.log('✅ Added session_id to request:', sessionId)
  } else {
    console.log('❌ No session_id to add to request')
  }
  let responseHtml =
    '<p>Apologies, something went wrong while contacting the assistant.</p>'

  if (images && images.length > 0) {
    // if (images?.length) {
    //   [mockData[3][1].value.Img.src] = images
    //   const changes = await this.sdk.addMultipleItems([mockData[3]])
    //   this.call('animateMultiple', changes)
    //   return `<p><b>${mockData[3][1].key}</b> component with image added to the Canvas.</p>`
    // }
    const imageDataUrl = images[0] // Assuming images[0] is a data URL
    // Example: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUg..."
    const parts = imageDataUrl.split(',')
    if (
      parts.length === 2 &&
      parts[0].includes('data:') &&
      parts[0].includes('base64')
    ) {
      const mimePart = parts[0].substring(
        parts[0].indexOf(':') + 1,
        parts[0].indexOf(';')
      )
      requestBody.image_data = parts[1]
      requestBody.image_mime_type = mimePart
      console.log(`Sending image with MIME type: ${mimePart}`)
    } else {
      console.error(
        'Invalid or unsupported image data URL format:',
        imageDataUrl.substring(0, 100)
      )
      // Optionally, inform the user or proceed without image data
    }
  }

  try {
    console.log('Sending request to API:', JSON.stringify(requestBody, null, 2))
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    })

    const responseText = await response.text() // Get raw response text for debugging
    console.log('Raw API Response:', responseText)

    if (!response.ok) {
      let errorDetail = 'Unknown error'
      try {
        const errorData = JSON.parse(responseText)
        errorDetail = errorData.detail || errorData.error || response.statusText
      } catch (e) {
        errorDetail = response.statusText || 'Failed to parse error response'
      }
      console.error('API Error:', response.status, errorDetail)
      responseHtml = `<p>Error from assistant: ${errorDetail}</p>`
      return responseHtml
    }

    try {
      const data = JSON.parse(responseText)
      console.log('Parsed API Data:', data)
      console.log('🔍 Checking for session_id in response...')
      console.log('🔍 Full response structure:', JSON.stringify(data, null, 2))

      // Try to extract session_id from different possible locations in the response
      let sessionId = null

      console.log('🔍 DEBUG: data.session_id value:', data.session_id)
      console.log('🔍 DEBUG: typeof data.session_id:', typeof data.session_id)
      console.log('🔍 DEBUG: data.session_id truthy?', !!data.session_id)

      // Check top level
      if (data.session_id) {
        sessionId = data.session_id
        console.log('✅ Found session_id at top level:', sessionId)
      }
      // Check if it's nested in code_response
      else if (data.code_response && data.code_response.session_id) {
        sessionId = data.code_response.session_id
        console.log('✅ Found session_id in code_response:', sessionId)
      }
      // Check if it's nested in response
      else if (data.response && data.response.session_id) {
        sessionId = data.response.session_id
        console.log('✅ Found session_id in response:', sessionId)
      } else {
        console.warn('⚠️ No session_id found in API response!')
        console.warn('⚠️ Available response keys:', Object.keys(data))
        console.warn('⚠️ data.session_id specifically:', data.session_id)
        console.warn('⚠️ Response structure:', JSON.stringify(data, null, 2))
      }

      // Store session ID if found
      if (sessionId) {
        console.log('✅ Found session_id, storing it...')
        storeSessionId(sessionId)
        console.log(
          '✅ Session continuity enabled - future requests will use session:',
          sessionId
        )
      }

      return data
    } catch (e) {
      console.error('Error parsing API response:', e)
      return responseText // Return raw response if parsing fails
    }
  } catch (error) {
    console.error('Failed to fetch from API or process response:', error)
    return `<p>Failed to connect to the assistant: ${error.message}</p>`
  }
}

export function parseAIAnswer(message) {
  // Handle our new, structured JSON response
  if (message?.code_response?.type === 'json_response') {
    const data = message.code_response.data
    console.log('Parsing json_response:', data)

    // The AI has returned a structured asset. We can now process it.
    // The goal is to return an object that the calling component can use
    // to either render something on the canvas or display information.
    return {
      asset: data, // Pass the whole asset data,
      type: data.type + 's',
      message:
        data.description || `Successfully generated ${data.type}: ${data.title}`
    }
  }

  if (message?.code_response?.type === 'symbols_component_code') {
    const data = message.code_response.component_code
    const parsed = JSON.parse('[' + data + ']')

    return {
      asset: parsed,
      multiple: true,
      message: `Successfully generated multiple items `
    }
  }

  // Fallback for plain text or error responses from the Python server
  if (message?.code_response?.type === 'plain_text_response') {
    return { message: message.code_response.content }
  }
  if (message?.code_response?.type === 'error') {
    return { message: `An error occurred: ${message.code_response.content}` }
  }

  // Legacy handling for older response formats, if any are still in use.
  if (message && message.response) {
    const responseData = message.response
    if (responseData.type === 'greeting') {
      return {
        message: responseData
      }
    } else if (responseData.type === 'text_response' && responseData.content) {
      const content = responseData.content
      const parts = content.split(/(```(?:js|javascript)?\n[\s\S]*?```)/g)

      const children = parts
        .filter((part) => part)
        .map((part) => {
          if (part.startsWith('```')) {
            const code = part
              .replace(/```(js|javascript)?\n/, '')
              .replace(/```$/, '')
            return {
              code
            }
          } else {
            return part
              .split(/(### .*)/g)
              .filter((p) => p)
              .map((textPart) => {
                if (textPart.startsWith('### ')) {
                  return {
                    message: textPart.substring(4)
                  }
                }
                return {
                  message: textPart
                }
              })
          }
        })
        .flat()

      return {
        children
      }
    }
  }

  console.log('Unhandled message format:', message)
  return { message: 'Received an unrecognized response from the assistant.' }
}

export function animateMultiple(changes) {
  const jointData = []
  changes.forEach((item, i) => {
    if (i % 2 === 0) {
      const [, , value] = item
      jointData.push({ value })
    } else {
      const [, , schema] = item
      const currentItem = jointData[jointData.length - 1]
      jointData[jointData.length - 1] = { ...currentItem, ...schema }
    }
  })
  console.log(jointData)
  jointData.forEach(async (item, i) => {
    // const t = setTimeout(async () => {
    // await this.addComponentOnCanvas(item)
    // clearTimeout(t)
    // }, i * 350)
  })
}

export async function setPrompt(msg) {
  const AIMessage = this.getFilesSidebarPrompt().Label.Span
  await AIMessage.state.update({
    keyword: msg
  })
  const t = setTimeout(() => {
    AIMessage.update({
      html: AIMessage.state.keyword + '&nbsp;'
    })
    AIMessage.node.focus()
    const t2 = setTimeout(() => {
      // select all the content in the element
      document.execCommand('selectAll', false, null)
      // collapse selection to the end
      document.getSelection().collapseToEnd()
      clearTimeout(t2)
    }, 35)
    clearTimeout(t)
  }, 35)
}

// Message type handlers
export function handleGreetingResponse(responseData) {
  return {
    flow: 'y',
    gap: 'A',
    padding: 'Y2 A',
    shape: 'bubble',
    shapeDirection: 'top right',
    round: 'C X C C',
    theme: 'dialog',
    maxWidth: 'I1',
    children: [
      {
        extends: 'P',
        text: responseData.message,
        fontWeight: '500'
      },
      {
        extends: 'P',
        text: responseData.capabilities_summary,
        margin: 'A 0 Z',
        color: 'caption'
      },
      ...responseData.categories.map((category) => ({
        flow: 'y',
        gap: 'Z1',
        children: [
          {
            extends: 'H6',
            text: category.name,
            fontSize: 'Z1',
            margin: '0 0 X'
          },
          {
            flow: 'y',
            gap: 'X',
            children: category.items.map((item) => ({
              extends: 'P',
              text: `- ${item}`,
              color: 'caption',
              fontSize: 'Z'
            }))
          }
        ]
      }))
    ]
  }
}

export function handleTextResponse(content) {
  const parts = content.split(/(```(?:js|javascript)?\n[\s\S]*?```)/g)
  const children = parts
    .filter((part) => part)
    .map((part) => {
      if (part.startsWith('```')) {
        return formatCodeBlock(part)
      }
      return formatTextParts(part)
    })
    .flat()

  return {
    flow: 'y',
    gap: 'A',
    padding: 'Y2 A',
    shape: 'bubble',
    shapeDirection: 'top right',
    round: 'C X C C',
    theme: 'dialog',
    maxWidth: '85%',
    children
  }
}

export function handleCodeResponse(message) {
  const { analysis_text, symbols_code_response, type } =
    message.code_response || {}
  let component_code = symbols_code_response
    ? symbols_code_response.component_code
    : type === 'symbols_component_code'
      ? message.code_response.component_code
      : ''

  if (component_code) {
    component_code = sanitizeComponentCode(component_code)
  }

  if (!component_code && !analysis_text) {
    return {
      extends: 'P',
      text: 'Invalid code response format.'
    }
  }

  return buildCodeResponseUI(analysis_text, component_code)
}

// Helper functions
export function formatCodeBlock(part) {
  const code = part.replace(/```(js|javascript)?\n/, '').replace(/```$/, '')
  return {
    extends: 'P',
    text: code,
    background: 'rgba(0,0,0,.1)',
    padding: 'A',
    round: 'Z2',
    fontSize: 'Z',
    fontFamily: 'monospace',
    margin: 'A 0',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all'
  }
}

export function formatTextParts(part) {
  return part
    .split(/(### .*)/g)
    .filter((p) => p)
    .map((textPart) => {
      if (textPart.startsWith('### ')) {
        return {
          extends: 'H6',
          text: textPart.substring(4),
          margin: 'A 0 Z',
          fontWeight: 'bold'
        }
      }
      return {
        extends: 'P',
        text: textPart,
        color: 'caption',
        fontSize: 'Z',
        lineHeight: '1.5'
      }
    })
}

export function sanitizeComponentCode(code) {
  return code.replace(/(}\s*)\]/g, '$1}').replace(/,\s*([}\]])/g, '$1')
}

export function buildCodeResponseUI(analysis_text, component_code) {
  try {
    return {
      flow: 'x',
      contentEditable: false,
      shape: 'bubble',
      shapeDirection: 'top right',
      round: 'C X C C',
      theme: 'dialog',
      padding: 'Y2 A',
      margin: '- -Z',
      maxWidth: '85%',
      children: [
        analysis_text && buildAnalysisSection(analysis_text),
        component_code && buildCodeSection(component_code)
      ].filter(Boolean)
    }
  } catch (e) {
    console.error('Failed to parse Symbols component:', e)
    return {
      extends: 'P',
      text: `Error rendering component: ${e.message}`
    }
  }
}

export function buildFallbackMessage(message) {
  return {
    extends: 'AIMessage',
    contentEditable: false,
    shape: 'bubble',
    shapeDirection: 'top right',
    round: 'C X C C',
    theme: 'dialog',
    padding: 'Y2 A',
    color: 'paragraph',
    maxWidth: 'I1',
    html: (el, s) =>
      typeof message === 'object'
        ? `<pre>${JSON.stringify(message, null, 2)}</pre>`
        : message
  }
}

export async function submitPrompt() {
  const el = this
  const s = this.state
  const value = s.keyword
  if (!value) return

  const images = s.images
  s.apply((s) => {
    s.thread.push({ role: 'user', message: value, images: [...images] })
    s.images = []
    s.keyword = ''
  })
  el.Flex.Span.node.innerText = ''

  // Add a temporary "thinking" message for better UX
  s.thread.push({ role: 'agent', isThinking: true, message: '...' })
  s.update()

  const selectedModel = s.model || 'Starter' // Default to Free if no model selected
  const res = await el.call('giveMeAnswer', value, images, selectedModel)
  console.log('res', res)
  const parsed = await el.call('parseAIAnswer', res)
  console.log('Parsed AI Answer:', parsed)

  if (parsed.asset.type) {
    parsed.asset.type = el.call('getPlural', parsed.asset.type)
  }
  // Remove "thinking" message
  s.thread.pop()

  if (
    parsed?.asset &&
    (parsed.asset.type === 'components' || parsed.asset.type === 'pages')
  ) {
    console.log('Rendering asset:', parsed.asset.title)
    try {
      const asset = parsed.asset
      const assetType = asset.type // 'components' or 'pages'

      // First, add the item to the main data store via the SDK
      const res = el.call('createItem', `${assetType}`, asset.key, asset)
      console.log(asset)

      if (res) {
        // Add the AI response with both message and component code
        s.thread.push({
          role: 'agent',
          message: parsed.message || `I've created a ${asset.title} for you.`,
          analysis_text: asset.description,
          component_code: JSON.stringify(asset.value, null, 2),
          component: true
        })
      } else {
        // Handle cases where the SDK fails to add the item
        throw new Error(`Could not add ${assetType} '${asset.title}'`)
      }
    } catch (e) {
      console.error('Error adding or rendering component:', e)
      s.thread.push({
        role: 'agent',
        message: `Error adding component: ${e.message}`
      })
    }
  } else if (parsed.multiple) {
    const asset = parsed.asset
    const res = el.call('createMultipleItems', asset)

    if (res) {
      // Add the AI response with both message and component code
      s.thread.push({
        role: 'agent',
        message: parsed.message || `I've created a ${asset.title} for you.`,
        analysis_text: asset.description,
        component_code: JSON.stringify(asset.value, null, 2),
        component: true
      })
    }
  } else if (parsed) {
    // For other assets or text, display the information.
    s.thread.push({
      role: 'agent',
      analysis_text: parsed.message,
      component_code: parsed.asset
        ? JSON.stringify(parsed.asset, null, 2)
        : null,
      component: !!parsed.asset // Trigger the component renderer path
    })
  } else {
    s.thread.push({
      role: 'agent',
      message: 'Sorry, I could not process that request.'
    })
  }
  if (s.activeContent !== 'AI') {
    s.update({ activeContent: 'AI' })
  }
}

/*
<!-- page -->
{
  key: '/about',
  title: 'About us page',
  type: 'page',
  description: '...',
  value: { ... },
}

<!-- component -->
{
  key: 'Header',
  title: 'Header component',
  type: 'components',
  description: '...',
  value: { ... },
}

<!-- function -->
{
  type: 'function',
  key: 'makeFn',
  title: 'Making function',
  description: '...',
  value: () => { ... },
}

<!-- method -->
{
  type: 'method',
  key: 'makeFn',
  title: 'Making method',
  description: '...',
  value: () => { ... },
}

<!-- dependency -->
{
  type: 'dependency',
  key: 'smbls',
  version: 'latest',
}

<!-- state -->
{
  type: 'state',
  key: 'products',
  value: [{ key: 'product1', ... }, { product2 }],
}

<!-- file -->
{
  type: 'file',
  key: 'some.jpg',
  value: 'url or base64', // need to check with @Thomas-sls about blob
}

<!-- color -->
{
  type: 'color',
  key: 'blue',
  value: '#123',
}

<!-- semantic colors (dark-light) -->
{
  type: 'color',
  key: 'call-to-action',
  value: {
    '@dark': '#123',
    '@light': '#321'
  },
}

<!-- icon -->
{
  type: 'icon',
  key: 'arrowRight',
  value: '<svg>...</svg>',
}

<!-- media query -->
{
  type: 'media',
  key: '@mobileXs',
  value: '(min-width: 100px)',
}

<!-- font -->
{
  type: 'font',
  key: 'FiraGO',
  value: [{
    // regular,
    url: 'https://cdn.jsdelivr.net/fontsource/fonts/firago@latest/latin-100-normal.woff2', // or base64,
    fontWeight: '400',
    fontStyle: 'normal',
    unicodeRange: 'latin',
  }, {
    // bold,
    url: 'https://cdn.jsdelivr.net/fontsource/fonts/firago@latest/latin-200-normal.woff2', // or base64,
    fontWeight: '700',
    fontStyle: 'normal',
    unicodeRange: 'latin',
  }],
}

<!-- font-family (often coupled with font) -->
{
  type: 'font_family',
  key: 'firaGo',
  key: 'FiraGO',
  familyName: 'FiraGO',
  type: 'serif',
  value: [
    'FiraGO', // maps with font name above
    'Helvetica, Arial, sans-serif, --system-default',
  ]
}

<!-- theme -->
{
  key: 'primary',
  type: 'theme',
  value: {
    color: '...',
    background: '...',
  }
}
*/
